'use client';

import React, { useId, useRef, useState } from 'react';

import Link from 'next/link';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { CompactRelativeTimestamp } from '@/components/CompactRelativeTimestamp';
import {
  appendComposeDraft,
  ComposeBox,
  type ComposeBoxHandle,
  type ComposeBoxPrefillIntent,
} from '@/components/ComposeBox';
import { OfferImage } from '@/components/OfferMiniCard';
import { SuggestedQuestionChips } from '@/components/SuggestedQuestionChips';
import { IdentityChipGroup, IdentityRow, identityFromDocProvenance } from '@/components/identity';
import { FEEDBACK_TRIGGERS, targetFeedItem, useFeedback } from '@/feedback';
import { ASPECT_REVIEW, ASPECT_QUESTION } from '@/lib/consts';
import { DISPLAY_IMAGE_WIDTHS } from '@/lib/displayImage';
import { extractPlainText } from '@/lib/structuredDocUtils';
import { isLLM } from '@/lib/utils';

import {
  DOC_VISIBILITY,
  DisplayDoc,
  DisplayUser,
  FeedItem,
  type FeedItemSuggestedQuestion,
  normalizeThreadNodes,
  THREAD_NODE_KIND,
  ThreadNode,
} from '../lib/api';

type ThreadFeedItemProps = {
  item: FeedItem;
  showTopic: boolean;
  currentUser?: DisplayUser | null;
  /** Controls whether participant identities render inline or at the card's bottom right. */
  participantPlacement?: 'inline' | 'bottom-right';
};

/** Returns a truncated preview from structured content when available, otherwise plain text. */
const getPreviewText = (doc: DisplayDoc, maxLength: number): string => {
  const fullText = doc.structured_content ? extractPlainText(doc.structured_content) : doc.text;
  return fullText.length > maxLength ? `${fullText.substring(0, maxLength)}...` : fullText;
};

export const ThreadFeedItem: React.FC<ThreadFeedItemProps> = ({
  item,
  showTopic,
  currentUser,
  participantPlacement = 'inline',
}) => {
  const threadId = useId();
  const { openFeedback } = useFeedback();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(false);
  /** Mirrors the composer draft so replacing it with a suggestion is never destructive by surprise. */
  const [composeDraft, setComposeDraft] = useState('');
  /** Supplies question-specific posting context while the user edits a suggestion. */
  const [editedQuestion, setEditedQuestion] = useState<FeedItemSuggestedQuestion | null>(null);
  /** Issues one-shot content and caret updates to the composer. */
  const [composePrefill, setComposePrefill] = useState<ComposeBoxPrefillIntent | null>(null);
  /** Holds a selected question until the user chooses how to handle a dirty draft. */
  const [pendingQuestion, setPendingQuestion] = useState<FeedItemSuggestedQuestion | null>(null);
  /** Gives each accepted prefill a key that remains unique for this mounted feed item. */
  const composePrefillSequenceRef = useRef(0);
  /** Restores focus after the draft-replacement confirmation closes. */
  const composeBoxRef = useRef<ComposeBoxHandle>(null);

  /** Replaces the composer with a question and moves focus to the end of its text. */
  const applyQuestionToComposer = (question: FeedItemSuggestedQuestion): void => {
    composePrefillSequenceRef.current += 1;
    setEditedQuestion(question);
    setComposeDraft(question.text);
    setComposePrefill({
      key: `suggested-question-${composePrefillSequenceRef.current}`,
      content: question.text,
      selectionStart: question.text.length,
      selectionEnd: question.text.length,
    });
    setPendingQuestion(null);
  };

  /** Prefills immediately unless doing so would discard a user-authored draft. */
  const handleEditQuestion = (question: FeedItemSuggestedQuestion): void => {
    const hasDraft = composeDraft.trim().length > 0;
    const isUnmodifiedSuggestion = editedQuestion !== null && composeDraft === editedQuestion.text;
    if (!hasDraft || isUnmodifiedSuggestion) {
      applyQuestionToComposer(question);
      return;
    }

    setPendingQuestion(question);
  };

  /** Collapses the panel and clears the draft state owned by its unmounted composer. */
  const closeSuggestedQuestions = (): void => {
    setShowSuggestedQuestions(false);
    setComposeDraft('');
    setEditedQuestion(null);
    setComposePrefill(null);
    setPendingQuestion(null);
  };

  /** Preserves the current draft and returns keyboard focus to its composer. */
  const keepComposeDraft = (): void => {
    setPendingQuestion(null);
    composeBoxRef.current?.focus();
  };

  /** Adds the selected question after the current draft and moves focus to the new end. */
  const appendQuestionToComposer = (question: FeedItemSuggestedQuestion): void => {
    const appendedDraft = appendComposeDraft(composeDraft, question.text);
    composePrefillSequenceRef.current += 1;
    setEditedQuestion(question);
    setComposeDraft(appendedDraft);
    setComposePrefill({
      key: `suggested-question-${composePrefillSequenceRef.current}`,
      content: appendedDraft,
      selectionStart: appendedDraft.length,
      selectionEnd: appendedDraft.length,
    });
    setPendingQuestion(null);
  };

  if (!item) return null;
  const thread = item.thread;
  if (!thread) return null;
  /** Treats malformed runtime entries as absent from the rendered thread. */
  const nodes = normalizeThreadNodes(thread.nodes);
  if (nodes.length === 0) return null;
  const docs = nodes.flatMap((node) => (node.kind === THREAD_NODE_KIND.Document ? [node.doc] : []));
  if (docs.length === 0) return null;
  /** Uses the newest readable position as the parent for a manual follow-up question. */
  const replyTargetDoc = docs[docs.length - 1];

  // Find the latest doc from a human user (non-LLM) from the supplied docs
  const latestUserDoc = (() => {
    for (let i = docs.length - 1; i >= 0; i--) {
      const doc = docs[i];
      if (!isLLM(doc.provenance.creator)) {
        return doc;
      }
    }
    // Fallback to the last supplied doc
    return docs[docs.length - 1];
  })();

  // Is there a review or question in the supplied docs?
  const hasReview = docs.some((doc) => doc.aspect?.cref === ASPECT_REVIEW);
  const hasQuestion = docs.some((doc) => doc.aspect?.cref === ASPECT_QUESTION);

  const protectedDocPath = `/doc/${latestUserDoc.id}`;
  const href_target = currentUser
    ? protectedDocPath
    : item.public_review_id
      ? `/review/${item.public_review_id}`
      : `/login?${new URLSearchParams({ redirect: protectedDocPath }).toString()}`;
  const feedbackTargetId = item.summary?.id ?? latestUserDoc.id;
  const topic = latestUserDoc.topic?.display || '';
  const displayedOffers = item.offers.slice(0, 3);
  const hasSuggestedQuestions =
    Boolean(currentUser) && item.justification.is_owned && item.suggested_questions.length > 0;
  const suggestedQuestionsId = `suggested-questions-${threadId}`;

  const just = item.justification;

  const participantIdentities = Array.from(
    new Map(
      docs
        .map((doc) => identityFromDocProvenance(doc.provenance))
        .filter((identity) => identity.username !== currentUser?.username)
        .filter((identity) => !isLLM(identity.username))
        .map((identity) => [identity.username, identity])
    ).values()
  );

  /** Derives the header text and whether it contains the document topic. */
  const deriveHeader = (): { text: string; includesDocTopic: boolean } => {
    let result: string = '';
    if (hasReview) {
      result += `Review `;
    } else if (hasQuestion) {
      result += `Question `;
    }
    // For non-logged-in users
    if (!currentUser) {
      if (result.length > 0) {
        return { text: `${result}${topic || 'Thread'}`, includesDocTopic: topic.length > 0 };
      }
      return { text: topic || 'Thread', includesDocTopic: topic.length > 0 };
    }

    // Review and question classification remains visible regardless of why the social item appears.
    if (result.length > 0) {
      return { text: result, includesDocTopic: false };
    }

    // For logged-in users with justification
    if (just.mentioner) {
      return { text: '', includesDocTopic: false };
    }
    if (result.length === 0 && just.topic) {
      result += `Your interests: ${just.topic.display}`;
      return {
        text: result,
        includesDocTopic: topic.length > 0 && just.topic.cref === latestUserDoc.topic?.cref,
      };
    }
    if (just.friend) {
      return { text: '', includesDocTopic: false };
    }
    if (just.is_owned) {
      return { text: topic, includesDocTopic: topic.length > 0 };
    }
    return { text: topic || 'Thread', includesDocTopic: topic.length > 0 };
  };

  /** Stores the derived left-side header text and topic inclusion state. */
  const header = deriveHeader();
  /** Indicates whether the right topic renders when enabled, present, and absent from the left header. */
  const shouldShowRightTopic = showTopic && topic.length > 0 && !header.includesDocTopic;
  const hasVisibleHeader = header.text.length > 0 || shouldShowRightTopic;

  /** Renders the flexible header row with its optional right topic label. */
  const renderHeader = (): React.ReactNode => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
        <Typography variant="body2" component="span" color="text.secondary">
          {header.text}
        </Typography>
      </Box>
      {shouldShowRightTopic ? (
        <Typography
          variant="body2"
          component="span"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 1, minWidth: 0, maxWidth: '40%' }}
        >
          {topic}
        </Typography>
      ) : null}
    </Box>
  );

  /** Renders one structural position without exposing metadata for inaccessible nodes. */
  const renderReplyNode = (node: ThreadNode): React.JSX.Element => {
    switch (node.kind) {
      case THREAD_NODE_KIND.Document: {
        const identity = identityFromDocProvenance(node.doc.provenance);
        return (
          <ListItem
            key={node.public_doc_id}
            disableGutters
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}
          >
            <IdentityRow identity={identity} linkMode="text" avatarSize={24} />
            <Typography variant="body2" component="span">
              {getPreviewText(node.doc, 450)}
            </Typography>
          </ListItem>
        );
      }
      case THREAD_NODE_KIND.Inaccessible:
        return (
          <ListItem key={node.public_doc_id} disableGutters sx={{ py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Post unavailable
            </Typography>
          </ListItem>
        );
      default: {
        const exhaustiveNode: never = node;
        return exhaustiveNode;
      }
    }
  };

  /** Renders the backend-selected chronological reply positions without client-side truncation. */
  const renderReplyChain = (): React.JSX.Element => {
    return (
      <Box component="section" aria-label="Reply chain" sx={{ mt: 1 }}>
        <List dense disablePadding>
          {nodes.map(renderReplyNode)}
        </List>
      </Box>
    );
  };

  /** Opens the feed-card overflow menu without activating the thread link. */
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  /** Closes the feed-card overflow menu. */
  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  /** Opens feedback for this feed card without navigating its primary link. */
  const handleFeedbackOpen = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    handleMenuClose();
    openFeedback({ trigger: FEEDBACK_TRIGGERS.FLAG, target: targetFeedItem(feedbackTargetId) });
  };

  /** Renders up to three backend-ranked offers without fabricating concept metadata. */
  const renderOfferMosaic = (): React.ReactNode => {
    if (displayedOffers.length === 0) return null;

    const hasMultipleOffers = displayedOffers.length > 1;
    const hasThreeOffers = displayedOffers.length === 3;
    return (
      <Box
        data-testid="feed-offer-mosaic"
        sx={{
          display: 'grid',
          gridTemplateColumns: hasMultipleOffers ? '2fr 1fr' : '1fr',
          gridTemplateRows: hasThreeOffers ? '1fr 1fr' : '1fr',
          gap: 0.5,
          width: { xs: '112px', sm: '160px' },
          minHeight: { xs: '84px', sm: '120px' },
          alignSelf: 'stretch',
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: 'var(--mui-palette-background-paper)',
        }}
      >
        {displayedOffers.map((offer, index) => (
          <Box
            key={offer.id}
            data-testid={`feed-offer-slot-${index + 1}`}
            data-offer-id={offer.id}
            sx={{
              position: 'relative',
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
              backgroundColor: 'var(--mui-palette-background-default)',
              ...(hasThreeOffers && index === 0 ? { gridRow: '1 / span 2' } : {}),
            }}
          >
            <OfferImage
              sourceImage={offer.images[0]}
              alt={offer.title}
              requestWidth={DISPLAY_IMAGE_WIDTHS.thumbnail}
              sizes="(max-width: 599px) 112px, 160px"
              objectFit="cover"
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Card sx={{ position: 'relative', mb: 1, overflow: 'hidden' }}>
      <CardActionArea
        component={Link}
        href={href_target}
        aria-label={`View ${showTopic && topic.length > 0 ? topic : 'Thread'}`}
        data-testid="feed-card-link"
        sx={{ position: 'absolute', inset: 0, zIndex: 1 }}
      />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box
          component="article"
          aria-labelledby={`thread-${threadId}`}
          data-testid="feed-thread-content"
          sx={{ display: 'flex', flexDirection: 'row' }}
        >
          <Box component={Typography} id={`thread-${threadId}`} sx={visuallyHidden}>
            Thread Content
          </Box>

          {renderOfferMosaic()}

          <Box
            data-testid="feed-card-body"
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0, p: 2 }}
          >
            {hasVisibleHeader ? <Box sx={{ mb: 1 }}>{renderHeader()}</Box> : null}
            <Box
              data-testid="feed-card-summary-row"
              sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {item.summary ? (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {item.summary.text}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body1" component="div">
                      <Typography component="span" fontWeight="bold">
                        {latestUserDoc.provenance.creator}
                      </Typography>{' '}
                      <CompactRelativeTimestamp timestamp={latestUserDoc.provenance.createdon} />{' '}
                      <Typography component="span" fontWeight="bold">
                        :
                      </Typography>{' '}
                      {getPreviewText(latestUserDoc, 450)}
                    </Typography>
                    {nodes.length > 1 && renderReplyChain()}
                  </>
                )}
                {participantPlacement === 'inline' && participantIdentities.length > 0 ? (
                  <Box
                    data-testid="feed-participants"
                    data-participant-placement="inline"
                    sx={{ position: 'relative', zIndex: 2, width: 'fit-content' }}
                  >
                    <IdentityChipGroup
                      identities={participantIdentities}
                      maxVisible={3}
                      chipProps={{ linkMode: 'link', atPrefix: true }}
                    />
                  </Box>
                ) : null}
                {just.mention_paragraph ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, whiteSpace: 'pre-line' }}
                  >
                    {just.mention_paragraph}
                  </Typography>
                ) : null}
              </Box>
              {currentUser ? (
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  aria-label="More options"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(menuAnchorEl)}
                  sx={{ position: 'relative', zIndex: 2, flexShrink: 0, mt: -0.5, mr: -0.5 }}
                >
                  <MoreVertIcon />
                </IconButton>
              ) : null}
            </Box>
            {hasSuggestedQuestions ? (
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  display: 'flex',
                  mt: 'auto',
                  pt: 1,
                  pointerEvents: 'none',
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  aria-expanded={showSuggestedQuestions}
                  aria-controls={suggestedQuestionsId}
                  aria-label={
                    showSuggestedQuestions ? 'Hide suggested questions' : 'Show suggested questions'
                  }
                  onClick={() => {
                    if (showSuggestedQuestions) {
                      closeSuggestedQuestions();
                    } else {
                      setShowSuggestedQuestions(true);
                    }
                  }}
                  sx={{ minWidth: 0, ml: 'auto', px: 0.5, py: 0.25, pointerEvents: 'auto' }}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: showSuggestedQuestions ? 'primary.main' : 'text.secondary',
                        transform: showSuggestedQuestions ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'color 150ms ease, transform 150ms ease',
                      }}
                    />
                  }
                >
                  More
                </Button>
              </Box>
            ) : null}
            {participantPlacement === 'bottom-right' && participantIdentities.length > 0 ? (
              <Box
                data-testid="feed-participants"
                data-participant-placement="bottom-right"
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  alignSelf: 'flex-end',
                  width: 'fit-content',
                  mt: 'auto',
                  pt: 1,
                }}
              >
                <IdentityChipGroup
                  identities={participantIdentities}
                  maxVisible={3}
                  chipProps={{ linkMode: 'link', atPrefix: true }}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      </CardContent>
      <Collapse
        in={hasSuggestedQuestions && showSuggestedQuestions}
        unmountOnExit
        id={suggestedQuestionsId}
        sx={{ position: 'relative', zIndex: 2 }}
      >
        <CardContent data-testid="suggested-questions-panel" sx={{ pt: 0 }}>
          <SuggestedQuestionChips
            questions={item.suggested_questions}
            onEdit={handleEditQuestion}
            visibility={replyTargetDoc.visibility ?? DOC_VISIBILITY.Public}
          />
          <Box sx={{ mt: 2 }}>
            {pendingQuestion ? (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => applyQuestionToComposer(pendingQuestion)}
                    >
                      Replace
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => appendQuestionToComposer(pendingQuestion)}
                    >
                      Append
                    </Button>
                    <Button color="inherit" size="small" onClick={keepComposeDraft}>
                      Keep draft
                    </Button>
                  </Box>
                }
              >
                <Typography variant="body2">
                  How would you like to use the selected question?
                </Typography>
              </Alert>
            ) : null}
            <ComposeBox
              ref={composeBoxRef}
              onClose={closeSuggestedQuestions}
              topic={
                editedQuestion
                  ? (editedQuestion.topic_cref ?? undefined)
                  : replyTargetDoc.topic?.cref
              }
              citationId={editedQuestion?.source_doc_id ?? replyTargetDoc.id}
              inheritedVisibility={replyTargetDoc.visibility ?? undefined}
              placeholder="Ask a follow-up question..."
              minRows={2}
              content={composeDraft}
              prefill={composePrefill ?? undefined}
              onContentChange={setComposeDraft}
            />
          </Box>
        </CardContent>
      </Collapse>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        disableEnforceFocus
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleFeedbackOpen}>Give feedback</MenuItem>
      </Menu>
    </Card>
  );
};
